import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { Search, X, UserPlus, MoreVertical, Filter, ArrowRight } from 'lucide-react';
import type { AdminOutletContext } from '../types';
import { MOCK_CLIENTS, Client } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import { useAgent } from '../context/AgentsContext';
import FlightQuoteModal from '../components/FlightQuoteModal';
import { loadClients, logClientCreated, saveClients } from '../lib/skyluxStore';

type StatusFilter = 'all' | Client['status'];

const statusStyle = (s: Client['status']) => {
  if (s === 'pending') return { background: '#fffbeb', color: '#b45309' };
  if (s === 'quoted') return { background: '#eff6ff', color: '#1d4ed8' };
  return { background: '#ecfdf5', color: '#047857' };
};

const AdminClients = () => {
  const { agentId } = useParams();
  const agent = useAgent(agentId);
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const [tableSearch, setTableSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [clients, setClients] = useState<Client[]>(() => loadClients(MOCK_CLIENTS));
  const [showAddClient, setShowAddClient] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteClient, setQuoteClient] = useState<Client | null>(null);
  const [preselectFlightId, setPreselectFlightId] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const f = params.get('flight');
    if (!f) return;
    setPreselectFlightId(f);
    setQuoteClient(null);
    setQuoteOpen(true);
    params.delete('flight');
    const path = window.location.pathname;
    const qs = params.toString();
    window.history.replaceState(null, '', qs ? `${path}?${qs}` : path);
  }, []);

  useEffect(() => {
    const fn = () => setClients(loadClients(MOCK_CLIENTS));
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, []);

  const upsertClient = (c: Client) => {
    setClients((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      const next = idx >= 0 ? prev.map((x) => (x.id === c.id ? c : x)) : [c, ...prev];
      saveClients(next);
      return next;
    });
  };

  const visibleClients = useMemo(() => {
    const qh = globalSearch.trim().toLowerCase();
    const ql = tableSearch.trim().toLowerCase();
    return clients.filter((c) => {
      const headerOk =
        !qh ||
        c.name.toLowerCase().includes(qh) ||
        c.email.toLowerCase().includes(qh) ||
        (c.origin && c.origin.toLowerCase().includes(qh));
      const localOk = !ql || c.name.toLowerCase().includes(ql) || c.email.toLowerCase().includes(ql);
      const statusOk = statusFilter === 'all' || c.status === statusFilter;
      return headerOk && localOk && statusOk;
    });
  }, [clients, globalSearch, tableSearch, statusFilter]);

  if (!agent) {
    return (
      <div className="animate-fade">
        <MissingAgentNotice />
      </div>
    );
  }

  return (
    <div className="animate-fade">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '2.25rem', fontWeight: '800', letterSpacing: '-0.025em' }}>Client Directory</h2>
          <p style={{ color: 'var(--text-muted)' }}>
            CRM: open a profile for notes, timeline, and quotes. Assign flights from inventory or search here as {agent.nickname}.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddClient(true)}
          style={{
            background: 'var(--accent)',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontWeight: '700',
          }}
        >
          <UserPlus size={20} /> Add Client
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: '1.5rem', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ position: 'relative', width: '300px', maxWidth: '100%' }}>
            <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="search"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search by name or email..."
              aria-label="Filter clients in table"
              style={{ paddingLeft: '2.75rem', background: '#f8fafc', height: '40px' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {(['all', 'pending', 'quoted', 'paid'] as StatusFilter[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: statusFilter === s ? 'var(--primary)' : '#f1f5f9',
                  color: statusFilter === s ? 'white' : 'var(--text-main)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <Filter size={16} /> {s === 'all' ? 'All statuses' : s}
              </button>
            ))}
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '1rem 2rem', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Client</th>
              <th style={{ padding: '1rem 2rem', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Route</th>
              <th style={{ padding: '1rem 2rem', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Status</th>
              <th style={{ padding: '1rem 2rem', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Added On</th>
              <th style={{ padding: '1rem 2rem', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleClients.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '2.5rem 2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No clients match your filters.
                </td>
              </tr>
            ) : (
              visibleClients.map((client) => (
                <tr key={client.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '1.25rem 2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: '#eff6ff',
                          color: 'var(--accent)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '700',
                        }}
                      >
                        {client.name[0] ?? '?'}
                      </div>
                      <div>
                        <Link to={`/admin/agent/${agentId}/clients/${client.id}`} style={{ fontWeight: '700', color: 'inherit', textDecoration: 'none' }}>
                          {client.name} →
                        </Link>
                        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{client.email || '—'}</p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1.25rem 2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600', fontSize: '0.875rem' }}>
                      {client.origin ?? '—'} <ArrowRight size={14} /> {client.destination ?? '—'}
                    </div>
                  </td>
                  <td style={{ padding: '1.25rem 2rem' }}>
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        ...statusStyle(client.status),
                      }}
                    >
                      {client.status.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '1.25rem 2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>{client.requestDate}</td>
                  <td style={{ padding: '1.25rem 2rem', textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setPreselectFlightId(null);
                          setQuoteClient(client);
                          setQuoteOpen(true);
                        }}
                        style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', background: 'var(--primary)', color: 'white', fontSize: '0.8125rem', fontWeight: '700' }}
                      >
                        Quote
                      </button>
                      <button type="button" style={{ padding: '0.5rem', borderRadius: '0.5rem', background: '#f1f5f9' }}>
                        <MoreVertical size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <FlightQuoteModal
        open={quoteOpen}
        onClose={() => {
          setQuoteOpen(false);
          setQuoteClient(null);
          setPreselectFlightId(null);
        }}
        agent={agent}
        client={quoteClient}
        onUpsertClient={upsertClient}
        preselectFlightId={preselectFlightId}
      />

      {showAddClient && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '2rem' }}>
          <div className="animate-fade" style={{ background: 'white', width: '100%', maxWidth: '520px', borderRadius: '2rem', padding: '2.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '800' }}>Add New Client</h3>
              <button type="button" onClick={() => setShowAddClient(false)}>
                <X size={24} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const id = `c_${Date.now()}`;
                const newClient: Client = {
                  id,
                  name: (formData.get('name') as string) || 'Unnamed',
                  email: (formData.get('email') as string) || '',
                  phone: (formData.get('phone') as string) || undefined,
                  origin: (formData.get('origin') as string) || undefined,
                  destination: (formData.get('destination') as string) || undefined,
                  requestDate: new Date().toISOString().split('T')[0],
                  status: 'pending',
                  source: 'manual',
                };
                upsertClient(newClient);
                logClientCreated(id, newClient.name);
                setShowAddClient(false);
              }}
              style={{ display: 'grid', gap: '1.25rem' }}
            >
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', display: 'block' }}>Full Name</label>
                <input name="name" type="text" placeholder="Jane Cooper" required />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', display: 'block' }}>Email (optional if unknown)</label>
                <input name="email" type="email" placeholder="jane@example.com" />
              </div>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', display: 'block' }}>Phone</label>
                <input name="phone" type="tel" placeholder="+1 …" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', display: 'block' }}>From</label>
                  <input name="origin" type="text" placeholder="City" />
                </div>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', display: 'block' }}>To</label>
                  <input name="destination" type="text" placeholder="City" />
                </div>
              </div>
              <button type="submit" style={{ background: 'var(--primary)', color: 'white', padding: '1rem', borderRadius: '1rem', fontWeight: '800', marginTop: '1rem' }}>
                CREATE CLIENT
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClients;
